import React from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { motion } from 'framer-motion'
import { RootState } from '../store'
import {
  ArrowRight,
  Trophy,
  Crown,
  // CHESS_HIDDEN: Swords,
  Users,
  BarChart3,
  Target,
  UserPlus,
  Gamepad2,
  Gift,
  Medal,
  Building2,
  Globe,
  Trees,
  CircleDot,
} from 'lucide-react'
import LandingNavbar from '../components/landing/Navbar'
import RoomsSection from '../components/RoomsSection'
// CHESS_HIDDEN: import ChessTournamentTypes from '../components/ChessTournamentTypes'
import Footer from '../components/Footer'
import LeagueSeasonBanners from '../components/landing/LeagueSeasonBanners'
import ChessArenasCorporateCta from '../components/landing/ChessArenasCorporateCta'
import AcademOneCorporateCta from '../components/landing/AcademOneCorporateCta'
import { useTranslation } from '../hooks/use-i18n'

const MotionLink = motion.create(Link)

/* breakroom-champions: animation variants for Features section */
const featuresContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
}
const featuresItem = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
}

const heroStats = [
  { value: '7+', labelKey: 'landing.stats.formats' as const, emoji: '🏆' },
  { value: '24/7', labelKey: 'landing.stats.live' as const, emoji: '⏰' },
  { value: '2 wk', labelKey: 'landing.stats.freeTrial' as const, emoji: '🎁' },
  { value: '∞', labelKey: 'landing.stats.members' as const, emoji: '👥' },
]

const features = [
  { icon: Trophy, titleKey: 'landing.features.sportsPredictions' as const, descKey: 'landing.features.sportsPredictionsDesc' as const, color: 'text-success', bg: 'bg-success/10' },
  { icon: Crown, titleKey: 'landing.features.formula1' as const, descKey: 'landing.features.formula1Desc' as const, color: 'text-destructive', bg: 'bg-destructive/10' },
  // CHESS_HIDDEN: in-app chess feature — play on chessarenas.com
  // { icon: Swords, titleKey: 'landing.features.chessArena' as const, descKey: 'landing.features.chessArenaDesc' as const, color: 'text-chess', bg: 'bg-chess/10' },
  { icon: Users, titleKey: 'landing.features.privateRooms' as const, descKey: 'landing.features.privateRoomsDesc' as const, color: 'text-primary', bg: 'bg-primary/10' },
  { icon: BarChart3, titleKey: 'landing.features.liveRankings' as const, descKey: 'landing.features.liveRankingsDesc' as const, color: 'text-warning', bg: 'bg-warning/10' },
  { icon: Target, titleKey: 'landing.features.publicArena' as const, descKey: 'landing.features.publicArenaDesc' as const, color: 'text-success', bg: 'bg-success/10' },
  { icon: Globe, titleKey: 'landing.features.publicFootball' as const, descKey: 'landing.features.publicFootballDesc' as const, color: 'text-success', bg: 'bg-success/10' },
  { icon: Globe, titleKey: 'landing.features.publicFormula1' as const, descKey: 'landing.features.publicFormula1Desc' as const, color: 'text-destructive', bg: 'bg-destructive/10' },
  { icon: Trees, titleKey: 'landing.features.virtualGolf' as const, descKey: 'landing.features.virtualGolfDesc' as const, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { icon: CircleDot, titleKey: 'landing.features.tennisPredictions' as const, descKey: 'landing.features.tennisPredictionsDesc' as const, color: 'text-amber-400', bg: 'bg-amber-500/10' },
]

const howItWorksSteps = [
  { icon: UserPlus, titleKey: 'landing.howItWorks.step1Title' as const, descKey: 'landing.howItWorks.step1Desc' as const, step: '01' },
  { icon: Gamepad2, titleKey: 'landing.howItWorks.step2Title' as const, descKey: 'landing.howItWorks.step2Desc' as const, step: '02' },
  { icon: Medal, titleKey: 'landing.howItWorks.step3Title' as const, descKey: 'landing.howItWorks.step3Desc' as const, step: '03' },
]

const HERO_FEATURES = [
  { key: 'pillFootball' as const, icon: Trophy, color: 'text-emerald-400', bg: 'bg-emerald-500/18' },
  { key: 'pillF1' as const, icon: Crown, color: 'text-red-400', bg: 'bg-red-500/18' },
  // CHESS_HIDDEN: { key: 'pillChess' as const, icon: Swords, color: 'text-violet-400', bg: 'bg-violet-500/18' },
  { key: 'pillPrivate' as const, icon: Building2, color: 'text-sky-400', bg: 'bg-sky-500/18' },
] as const

const APP_STORE_URL = 'https://apps.apple.com/ph/app/break-room-cup/id6738211668'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.breakroomcup.app'

interface LandingProps {
  /** Force the guest marketing view even when authenticated (used by the promo recorder). */
  forceGuest?: boolean
}

const Landing: React.FC<LandingProps> = ({ forceGuest = false }) => {
  const { isAuthenticated: authed } = useSelector((state: RootState) => state.auth)
  const isAuthenticated = authed && !forceGuest
  const { t } = useTranslation()

  return (
    <div className="min-h-screen bg-background overflow-x-clip">
      <LandingNavbar isAuthenticated={isAuthenticated} />

      {/* Hero - exact breakroom-champions: min-h-screen, gradient-bg, pill, headline, CTAs with icons, stats bar */}
      {!isAuthenticated && (
        <section className="relative min-h-screen flex flex-col gradient-bg overflow-x-clip pt-[3.75rem] xs:pt-[4.25rem] sm:pt-[4.5rem] pb-6 sm:pb-8">
          {/* Hero background image (3D wireframe: chess pieces, football, F1 car, shapes) */}
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <img
              src={`${process.env.PUBLIC_URL || ''}/hero-bg.jpg`}
              alt=""
              className="w-full h-full object-cover object-[center_32%] sm:object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background" />
          </div>
          {/* Decorative orbs */}
          <div className="absolute top-1/4 left-1/4 w-72 h-72 rounded-full bg-primary/10 blur-[120px] animate-float pointer-events-none" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-chess/10 blur-[120px] animate-float pointer-events-none" style={{ animationDelay: '3s' }} />

          <div className="container mx-auto px-4 xs:px-5 sm:px-6 text-center relative z-10 flex flex-col items-center w-full max-w-full min-w-0">
            <motion.div
              initial="hidden"
              animate="show"
              variants={{
                hidden: {},
                show: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
              }}
              className="flex flex-col justify-start items-center w-full max-w-3xl mx-auto min-w-0 pt-1 sm:pt-1.5"
            >
              <motion.div
                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
                className="flex flex-wrap items-center justify-center gap-2 xs:gap-3 mb-3 sm:mb-4 max-w-full"
              >
                <div className="inline-flex items-center gap-2 glass-card rounded-full px-3 xs:px-4 py-2 shrink-0">
                  <span className="w-2 h-2 rounded-full bg-success animate-pulse shrink-0" />
                  <span className="text-[clamp(0.875rem,0.3vw+0.8rem,1.0625rem)] font-semibold text-foreground/95 leading-none">
                    {t('landing.hero.livePill')}
                  </span>
                </div>
                <MotionLink
                  to="/showcase"
                  className="relative inline-flex items-center justify-center shrink-0 rounded-full px-3 xs:px-4 py-2 text-[clamp(0.875rem,0.3vw+0.8rem,1.0625rem)] font-semibold text-black leading-none overflow-hidden border border-emerald-300/80 shadow-md"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(187,247,208,0.98) 0%, rgba(52,211,153,0.95) 50%, rgba(167,243,208,0.98) 100%)',
                  }}
                  initial={{ boxShadow: '0 0 0 0 rgba(34,197,94,0.35)' }}
                  animate={{
                    boxShadow: [
                      '0 0 0 0 rgba(34,197,94,0.35)',
                      '0 0 0 10px rgba(34,197,94,0)',
                      '0 0 0 0 rgba(34,197,94,0)',
                    ],
                  }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut' }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <motion.span
                    aria-hidden
                    className="absolute inset-0 pointer-events-none overflow-hidden rounded-full opacity-60"
                  >
                    <motion.span
                      className="absolute top-0 h-full w-[45%] bg-white/40 blur-sm -skew-x-12"
                      initial={{ x: '-130%' }}
                      animate={{ x: ['-130%', '230%'] }}
                      transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.2 }}
                    />
                  </motion.span>
                  <span className="relative max-w-[11rem] xs:max-w-none text-center leading-tight">
                    {t('landing.hero.quickTour')}
                  </span>
                </MotionLink>
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 shrink-0 rounded-full px-3 xs:px-4 py-2 bg-white text-black text-[clamp(0.875rem,0.3vw+0.8rem,1.0625rem)] font-semibold leading-none border border-white/80 shadow-md hover:opacity-90 transition-opacity"
                  aria-label={t('landing.hero.appStore')}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0" fill="currentColor" aria-hidden>
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                  </svg>
                  {t('landing.hero.appStore')}
                </a>
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 shrink-0 rounded-full px-3 xs:px-4 py-2 bg-white text-black text-[clamp(0.875rem,0.3vw+0.8rem,1.0625rem)] font-semibold leading-none border border-white/80 shadow-md hover:opacity-90 transition-opacity"
                  aria-label={t('landing.hero.googlePlay')}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0" fill="currentColor" aria-hidden>
                    <path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.24-.84-.76-.84-1.35zm13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27zm-1.36-1.24L14.54 12l1.91-1.88L6.05 2.66l8.49 8.49zm1.35-1.35c.5.24.84.76.84 1.35 0 .59-.34 1.11-.84 1.35L16.81 12l2.27-2.27z" />
                  </svg>
                  {t('landing.hero.googlePlay')}
                </a>
              </motion.div>

              <motion.h1
                variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
                className="font-display font-bold leading-[1.15] sm:leading-tight mb-1.5 sm:mb-2 text-center max-w-3xl mx-auto break-words min-w-0 text-[clamp(1.35rem,2.8vw+1rem,3.35rem)] sm:text-[clamp(1.6rem,4.2vw+1rem,3.5rem)]"
              >
                <span className="block text-foreground">{t('landing.hero.titleLine1')}</span>
                <span className="block mt-1 bg-gradient-to-r from-sky-400 via-violet-400 to-purple-500 bg-clip-text text-transparent">
                  {t('landing.hero.titleLine2')}
                </span>
              </motion.h1>

              <motion.div
                variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
                className="w-full max-w-lg mx-auto mt-3 mb-4 sm:mt-4 sm:mb-5 px-5 py-3.5 sm:px-7 sm:py-4 rounded-2xl sm:rounded-full text-center"
                style={{
                  backgroundColor: 'rgba(14, 165, 233, 0.14)',
                  border: '1px solid rgba(125, 211, 252, 0.38)',
                  boxShadow: '0 0 24px rgba(14, 165, 233, 0.18)',
                }}
              >
                <p className="font-display text-[1.08rem] sm:text-[1.28rem] font-semibold text-white leading-snug">
                  {t('landing.hero.taglineShare')}{' '}
                  <span className="font-extrabold text-[#4ADE80] [text-shadow:0_0_12px_rgba(74,222,128,0.45)]">
                    {t('landing.hero.pillFootball')}
                  </span>
                  {' '}{t('landing.hero.taglineAnd')}{' '}
                  <span className="font-extrabold text-[#F87171] [text-shadow:0_0_12px_rgba(248,113,113,0.4)]">
                    {t('landing.hero.pillF1')}
                  </span>
                </p>
                <p className="mt-1 font-display text-[0.95rem] sm:text-[1.08rem] font-medium text-white/80">
                  {t('landing.hero.taglineAudience')}
                </p>
              </motion.div>

              <ChessArenasCorporateCta />
              <AcademOneCorporateCta />

              <LeagueSeasonBanners />

              <motion.div
                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
                className="flex flex-col items-center gap-2 sm:gap-2.5 w-full max-w-full min-w-0"
              >
                <MotionLink
                  to="/login"
                  className="relative w-full xs:w-auto min-w-0 max-w-full inline-flex items-center justify-center gap-2 px-5 sm:px-8 py-3 sm:py-3.5 rounded-xl text-white font-semibold text-sm sm:text-nav shadow-lg break-words text-center overflow-hidden border border-orange-400/30"
                  style={{ backgroundColor: '#ff5630' }}
                  initial={{ boxShadow: '0 10px 28px rgba(255, 86, 48, 0.35)' }}
                  animate={{
                    boxShadow: [
                      '0 10px 28px rgba(255, 86, 48, 0.35)',
                      '0 14px 36px rgba(255, 86, 48, 0.55)',
                      '0 10px 28px rgba(255, 86, 48, 0.35)',
                    ],
                  }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.span
                    aria-hidden
                    className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl opacity-50"
                  >
                    <motion.span
                      className="absolute top-0 h-full w-[42%] bg-white/30 blur-sm -skew-x-12"
                      initial={{ x: '-130%' }}
                      animate={{ x: ['-130%', '230%'] }}
                      transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.25 }}
                    />
                  </motion.span>
                  <span className="relative z-10 min-w-0 break-words" style={{ overflowWrap: 'anywhere' }}>
                    {t('landing.hero.getStarted')}
                  </span>
                  <ArrowRight className="relative z-10 w-4 h-4 shrink-0" size={16} />
                </MotionLink>

                <p className="text-[11px] sm:text-xs text-white/75 text-center max-w-sm px-2">
                  {t('landing.hero.freeTrialSubtext')}
                </p>
              </motion.div>
            </motion.div>

          </div>
        </section>
      )}

      {/* Features - exact breakroom-champions: 6 cards, grid sm:grid-cols-2 lg:grid-cols-3 gap-5 */}
      {!isAuthenticated && (
        <section className="py-16 xs:py-20 sm:py-24 lg:py-32 gradient-bg-subtle relative overflow-hidden">
          <div className="container mx-auto px-3 xs:px-4 sm:px-6 w-full max-w-full">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-16"
            >
              <h2 className="font-display text-display-1 font-bold mb-4 text-foreground">
                {t('landing.features.title')} <span className="text-gradient">{t('landing.features.titleHighlight')}</span>
              </h2>
              <p className="text-body-lg text-muted-foreground max-w-lg mx-auto">
                {t('landing.features.subtitle')}
              </p>
            </motion.div>

            <motion.div
              variants={featuresContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4 xs:gap-5 sm:gap-6 lg:gap-8 max-w-4xl mx-auto"
            >
              {features.map((f) => (
                <motion.div
                  key={f.titleKey}
                  variants={featuresItem}
                  className="glass-card rounded-2xl p-5 xs:p-6 hover:bg-muted/10 transition-colors group min-w-0"
                >
                  <div className={`w-12 h-12 rounded-xl ${f.bg} flex items-center justify-center mb-4`}>
                    <f.icon className={f.color} size={22} />
                  </div>
                  <h3 className="text-display-2 font-semibold mb-2 text-foreground">{t(f.titleKey)}</h3>
                  <p className="text-body-lg text-muted-foreground leading-relaxed">{t(f.descKey)}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* When authenticated: Your Rooms section */}
      {isAuthenticated && (
        <main className="pt-20 xs:pt-24 pb-10 sm:pb-12">
          <div className="container mx-auto px-3 xs:px-4 sm:px-6 w-full max-w-full">
            <RoomsSection />
          </div>
        </main>
      )}

      {/* How It Works - breakroom-champions: scroll-in animations with stagger */}
      <section className="py-16 xs:py-20 sm:py-24 lg:py-32 relative overflow-hidden">
        <div className="container mx-auto px-3 xs:px-4 sm:px-6 w-full max-w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-display-1 font-bold mb-4 text-foreground">
              {t('landing.howItWorks.title')}
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-md mx-auto">
              {t('landing.howItWorks.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 xs:gap-6 max-w-4xl mx-auto">
            {howItWorksSteps.map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                className="glass-card rounded-2xl p-6 xs:p-8 text-center relative overflow-hidden group min-w-0"
              >
                <div className="absolute top-4 right-4 text-5xl font-display font-bold text-foreground/[0.04]">
                  {s.step}
                </div>
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
                  <s.icon className="text-primary" size={24} />
                </div>
                <h3 className="text-display-2 font-semibold mb-2 text-foreground">{t(s.titleKey)}</h3>
                <p className="text-body-lg text-muted-foreground leading-relaxed">{t(s.descKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - guests only (signed-in home is rooms on `/`; full tools live on `/dashboard`) */}
      {!isAuthenticated && (
        <section className="py-16 xs:py-20 sm:py-24 lg:py-32 relative overflow-hidden">
          <div className="container mx-auto px-3 xs:px-4 sm:px-6 w-full max-w-full">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="glass-strong rounded-2xl xs:rounded-3xl p-6 xs:p-8 sm:p-10 lg:p-14 text-center max-w-3xl mx-auto relative overflow-hidden min-w-0"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-chess/5 pointer-events-none" />
              <div className="relative z-10">
                <h2 className="font-display text-display-1 font-bold mb-4 text-foreground">
                  {t('landing.cta.title')}
                </h2>
                <p className="text-body-lg text-muted-foreground max-w-md mx-auto mb-4">
                  {t('landing.cta.subtitle')}
                </p>
                <p className="inline-flex items-center justify-center gap-1.5 rounded-full border border-emerald-400/35 bg-emerald-500/15 px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-bold uppercase tracking-wide text-emerald-300 mb-6">
                  <Gift className="w-3.5 h-3.5 shrink-0" aria-hidden />
                  {t('landing.hero.freeTrialBadge')}
                </p>
                <div className="flex flex-col xs:flex-row items-stretch xs:items-center justify-center gap-3 xs:gap-4">
                  <Link
                    to="/signup"
                    className="w-full xs:w-auto inline-flex items-center justify-center gap-2 px-6 xs:px-8 py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold text-nav hover:opacity-90 transition-all"
                  >
                    {t('landing.cta.getStarted')} <ArrowRight size={16} />
                  </Link>
                  <Link
                    to="/company-registration"
                    className="w-full xs:w-auto inline-flex items-center justify-center gap-2 px-6 xs:px-8 py-3.5 rounded-xl glass font-semibold text-nav hover:bg-muted/20 transition-all text-foreground"
                  >
                    <Building2 size={16} /> {t('landing.cta.forCompanies')}
                  </Link>
                </div>
                <p className="mt-4 text-xs sm:text-sm text-muted-foreground/90">
                  {t('landing.hero.freeTrialSubtext')}
                </p>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* CHESS_HIDDEN: Chess Tournament Types — play on chessarenas.com
      {!isAuthenticated && (
        <div className="container mx-auto px-3 xs:px-4 sm:px-6 pb-10 sm:pb-12 w-full max-w-full overflow-hidden">
          <ChessTournamentTypes />
        </div>
      )}
      */}

      <Footer showLinks className="mt-12 py-10" />
    </div>
  )
}

export default Landing
